"use client"

import { useMemo, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

export function Contact() {
  type SubmitState = 'idle' | 'success' | 'error'

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  })
  const [error, setError] = useState<string>("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitState, setSubmitState] = useState<SubmitState>('idle')
  const [statusMessage, setStatusMessage] = useState<string>("")

  const isValid = useMemo(() => {
    if (!formData.name.trim()) return false
    if (!formData.email.trim()) return false
    if (!formData.message.trim()) return false
    return true
  }, [formData])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isValid) {
      setError("Please enter your name, email, and message.")
      return
    }
    setError("")
    setSubmitState('idle')
    setStatusMessage("")
    setIsSubmitting(true)

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          message: formData.message.trim(),
        }),
      })

      const data = await response.json().catch(() => ({})) as { message?: string; error?: string }

      if (!response.ok) {
        throw new Error(data?.error || "Unable to send your message. Please try again.")
      }

      setSubmitState('success')
      setStatusMessage(data?.message || "Thanks for reaching out. Your message has been sent.")
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: ""
      })
      setError("")
    } catch (apiError: unknown) {
      setSubmitState('error')
      setError(apiError instanceof Error ? apiError.message : "Something went wrong. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card className="w-full max-w-md mx-auto bg-transparent border-none shadow-none">
      <CardHeader className="pb-4">
        <CardTitle className="text-3xl font-bold tracking-tight">Contact</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error ? (
            <div className="text-sm text-destructive">{error}</div>
          ) : null}
          {statusMessage ? (
            <div className={`text-sm ${submitState === 'success' ? 'text-emerald-600' : 'text-destructive'}`}>
              {statusMessage}
            </div>
          ) : null}
          <div>
            <Input
              placeholder="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          <div className="grid grid-cols-1 gap-3">
            <Input
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
            <Input
              placeholder="Phone (optional)"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              aria-label="Phone (optional)"
            />
          </div>
          <div>
            <Textarea
              placeholder="Message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="min-h-36"
              required
            />
          </div>
          <Button type="submit" className="w-full" disabled={!isValid || isSubmitting}>
            {isSubmitting ? "Sending..." : "Send Message"}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
} 